import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { AccordionVariant } from '../../../constants/AccordionVariant';
import {
  getHoverColorLocation,
  modifyLinks,
  getGlossaryLanguage,
  isItUndefined,
  translateStringUsingGlossary,
  getTarget,
} from '../../../util/DataUtil';
import {
  getItemValueByMachineName,
  getObjectValueByMachineName,
} from '../../../util/ObjectUtil';
import CtaBarButton from '../../base/cta-bar-button';
import FacilityModal from '../../modal/FacilityModal';
import { Constants, getBackendUrl } from '../../../util/Constants';
import Link from 'next/link';

const FacilityAccordion = ({
  title,
  description,
  index,
  checkedIcon,
  items,
  color,
  dataSet,
  subAccordion,
  version,
  cta,
  accordionVariant,
  titleTag,
  chipValue,
  spanColor,
  isIcon,
  bgColor,
  global,
  lang,
  emphasizedTextColor,
  isCertificationsPage,
  width,
}) => {
  const [hover, setHover] = useState(false);
  const [seeMore, setSeeMore] = useState(false);
  const [numberOfColumns, setNumberOfColumns] = useState(false);
  const [marketoTrigger, setMarketoTrigger] = useState(false);
  const [glossary, setGlossary] = useState([]);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const router = useRouter();
  const { locale } = router;

  const formatSlashText = (text) => {
    if (typeof text !== 'string' || text.length <= 10) return text;
    return text.replace(/\/(?=\S)/g, '/ ');
  };

  const renderTitle = (title, isCertificationsPage) => {
    if (isCertificationsPage) {
      return (
        <>
          <img src="/images/blue_checkmark.svg" />
          {title}
        </>
      );
    }
    return title;
  };
  let host = '';

  if (typeof window !== 'undefined') {
    host = getBackendUrl(window.location.host);
  }

  const fetchGlossaries = () => {
    if (lang && lang !== 'en') {
      fetch(
        host.baseApiUrlRemote + 'glossaries/v1/' + getGlossaryLanguage(lang)
      )
        .then((response) => response.json())
        .then((gloss) => {
          setGlossary(gloss);
        });
    } else {
      setGlossary([]);
    }
  };

  useEffect(() => {
    fetchGlossaries();
  }, [lang]);

  useEffect(() => {
    let columns;
    let width;

    if (typeof window !== 'undefined') {
      width = window.innerWidth;
    }

    switch (true) {
      case width < 417:
        columns = 1;
        break;
      case width >= 417 && width < 576:
        columns = 3;
        break;
      case width >= 576 && width < 1440:
        columns = 3;
        break;
      case width >= 1440 && width < 1920:
        columns = 2;
        break;
      case width >= 1920:
        columns = 3;
        break;
      default:
        columns = 1;
        break;
    }

    setNumberOfColumns(columns);
  }, []);

  useEffect(() => {
    // Reset the accordion state when the route changes
    const handleRouteChange = () => {
      setIsAccordionOpen(false);
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events]);

  const showSeeMore = () => {
    switch (numberOfColumns) {
      case 1:
        return items.length > 7;
      case 2:
        return items.length > 14;
      case 3:
        return items.length > 21;
      default:
        return items.length > 7;
    }
  };

  const handleSeeMore = () => {
    setSeeMore(!seeMore);
  };

  const renderSeeMoreText = () => {
    return seeMore ? 'See Less' : 'See More';
  };

  const renderCheckedIcon = (item) => {
    return (
      accordionVariant == AccordionVariant.STANDARD &&
      (item?.name || item?.value)
    );
  };

  const getValue = (item, key) => {
    if (typeof getItemValueByMachineName(item?.value, dataSet) === 'object') {
      return getItemValueByMachineName(item?.value, dataSet)?.map(
        (item, key) => {
          return (
            <div
              className={'accordion-item'}
              key={`accordion-item-${item?.name || item?.value || key}`}
            >
              {renderCheckedIcon(item) && (
                <img
                  className="accordion-icon"
                  src={checkedIcon}
                  alt={'icon'}
                />
              )}
              <span className="accordion-item-text">
                {formatSlashText(item?.name ? item?.name : item?.value)}
              </span>
            </div>
          );
        }
      );
    }
    if (typeof getItemValueByMachineName(item?.value, dataSet) === 'string') {
      return (
        <div className={'accordion-item'} key={`accordion-item-1`}>
          {renderCheckedIcon(item) && (
            <img className="accordion-icon" src={checkedIcon} alt={'icon'} />
          )}
          <span className="accordion-item-text">
            {formatSlashText(getItemValueByMachineName(item?.value, dataSet))}
          </span>
        </div>
      );
    }

    return (
      <div className={'accordion-item'} key={`accordion-item-${key}`}>
        {renderCheckedIcon(item) && (
          <img className="accordion-icon" src={checkedIcon} alt={'icon'} />
        )}
        <span className="accordion-item-text">
          {formatSlashText(item?.name ? item?.name : item?.value)}
        </span>
      </div>
    );
  };

  const getSubAccordionItems = (item, symbol) => {
    if (!item) return;

    if (typeof item == 'object') {
      return item?.map((e, key) => (
        <div
          className="sub-accordion-item"
          key={`sub-accordion-${e?.value || e?.name || key}`}
        >
          {renderCheckedIcon(e) && (
            <img className="sub-accordion-icon" src={checkedIcon} alt="icon" />
          )}
          <span className="sub-accordion-item-text">
            {e?.value ? e?.value : e?.name}
          </span>
        </div>
      ));
    }
    return (
      <div className="sub-accordion-item">
        {item && (
          <img className="sub-accordion-icon" src={checkedIcon} alt="icon" />
        )}
        <span className="sub-accordion-item-text">
          {item}
          {!item.includes('%') ? symbol : ''}
        </span>
      </div>
    );
  };

  const renderSubAccordion = (data) => {
    if (!data || data.length == 0) return;
    return data?.map((item, key) => {
      if (item.field_subtitle.length == 0) {
        return;
      }
      if (item?.field_item_value[0]?.value == '[[field_design_pue]]') {
        return;
      }
      return (
        <div
          className="heading-item"
          key={`heading-item-${item?.field_subtitle?.[0]?.value || key}`}
        >
          <span className="heading-title">
            {translateStringUsingGlossary(
              isItUndefined(item?.field_subtitle),
              glossary,
              getGlossaryLanguage(lang)
            )}
          </span>
          <div className={'sub-accordion-items'}>
            {item.field_item_value.map((e) => {
              return getSubAccordionItems(
                getItemValueByMachineName(e?.value, dataSet),
                item?.field_subtitle[0]?.value.includes('%') ||
                  item?.field_subtitle[0]?.value.includes('비율')
                  ? '%'
                  : ''
              );
            })}
          </div>
        </div>
      );
    });
  };

  const renderAccordionDetails = () => {
    return (
      <AccordionDetails>
        <span
          dangerouslySetInnerHTML={{ __html: modifyLinks(description, locale) }}
          className={`accordion-desc ${color} ${emphasizedTextColor ? emphasizedTextColor : ''}`}
        />

        {items?.length > 0 && (
          <div
            className={
              seeMore
                ? `accordion-items active ${showSeeMore() && 'see-more-active'}`
                : `accordion-items ${showSeeMore() && 'see-more-active'}`
            }
          >
            {items?.length > 0 &&
              items.map((item, key) => {
                if (!item) {
                  return;
                }
                return getValue(item, key);
              })}
          </div>
        )}

        {subAccordion?.length > 0 &&
          subAccordion[0]?.field_item_value.length > 0 && (
            <div
              className={`sub-accordion${version == '1' ? ' sub-accordion-list' : ''}`}
            >
              {version == 1 ? (
                renderSubAccordion(subAccordion)
              ) : (
                <div className="single">
                  {renderSubAccordion(subAccordion.slice(0, 1))}
                </div>
              )}
              {version !== 1 ? (
                <div className="multiple">
                  {renderSubAccordion(subAccordion.slice(1))}
                </div>
              ) : (
                ''
              )}
            </div>
          )}

        {showSeeMore() && (
          <div
            onClick={() => handleSeeMore()}
            className={showSeeMore() ? 'see-more active' : 'see-more'}
          >
            {showSeeMore() ? renderSeeMoreText() : ''}
          </div>
        )}

        {cta &&
          accordionVariant != AccordionVariant.CTA_WITH_HEAD_DESCRIPTION && (
            <CtaBarButton
              blockTypeClass={'facility-accordion'}
              bgColor={bgColor}
              data={cta}
              setMarketoTrigger={setMarketoTrigger}
              pathname={true}
            />
          )}
      </AccordionDetails>
    );
  };
  return (
    <>
      <Accordion
        className="accordion-control"
        expanded={isAccordionOpen}
        onChange={() => setIsAccordionOpen(!isAccordionOpen)}
      >
        <AccordionSummary
          aria-controls={`panel${index}a-content`}
          id={`panel${index}a-header`}
          sx={{
            flexDirection:
              accordionVariant == AccordionVariant.CTA_WITH_HEAD_DESCRIPTION
                ? 'row-reverse'
                : 'row',
          }}
          expandIcon={
            accordionVariant == AccordionVariant.CTA_WITH_HEAD_DESCRIPTION ? (
              <img
                style={
                  (hover
                    ? {
                        filter: getHoverColorLocation(
                          color == 'none' ? spanColor : color
                        ),
                      }
                    : null,
                  {
                    transform: isAccordionOpen
                      ? 'rotate(-90deg)'
                      : 'rotate(0deg)',

                    width: '32px',
                    height: '32px',
                  })
                }
                onMouseOut={() => setHover(false)}
                onMouseEnter={() => setHover(true)}
                src="/images/accordion-open.svg"
                alt={'arrow'}
              />
            ) : (
              <img
                style={
                  hover
                    ? {
                        filter: getHoverColorLocation(
                          color == 'none' ? spanColor : color
                        ),
                      }
                    : null
                }
                onMouseOut={() => setHover(false)}
                onMouseEnter={() => setHover(true)}
                src="/images/accordion-arrow.svg"
                alt={'arrow'}
              />
            )
          }
        >
          {isIcon == '1' &&
            accordionVariant == AccordionVariant.SECTION_WITH_CHIP && (
              <img
                className={`accordion-icon ${spanColor}`}
                src={checkedIcon}
                alt={'icon'}
              />
            )}
          {accordionVariant == AccordionVariant.SECTION_WITH_CHIP &&
            chipValue && (
              <div
                className={`chip-container ${spanColor}`}
                dangerouslySetInnerHTML={{
                  __html: getObjectValueByMachineName(chipValue, dataSet),
                }}
              ></div>
            )}
          <Typography
            component={titleTag === 'h3_class_styled' ? 'h3' : 'p'}
            className={`accordion-title${titleTag === 'h3_class_styled' ? ' h3-variant' : ''}`}
          >
            {renderTitle(title, isCertificationsPage)}
          </Typography>
          {cta && width <= 576 ? (
            <Link
              href={`${cta.field_cta_bar_link[0]?.value || cta.field_marketo_file_url[0]?.value}`}
            >
              <a
                style={{ marginLeft: 'auto' }}
                target={getTarget(
                  cta.field_cta_bar_link[0]?.value ||
                    cta.field_marketo_file_url[0]?.value
                )}
              >
                <img
                  // style={{ marginLeft: 'auto' }}
                  src="/images/cta-download-icon.svg"
                  alt="Download cta link"
                  width={32}
                  height={32}
                />
              </a>
            </Link>
          ) : (
            cta && (
              <CtaBarButton
                blockTypeClass={'facility-accordion'}
                bgColor={bgColor}
                data={cta}
                setMarketoTrigger={setMarketoTrigger}
                primaryText={isItUndefined(global.field_download_asset_text)}
                pathname={true}
                secondary={
                  accordionVariant == AccordionVariant.CTA_WITH_HEAD_DESCRIPTION
                }
              />
            )
          )}
        </AccordionSummary>
        {renderAccordionDetails()}
      </Accordion>
      {marketoTrigger && (
        <FacilityModal
          lang={lang}
          global={global}
          setTrigger={setMarketoTrigger}
          trigger={marketoTrigger}
        >
          {cta?.field_marketo_blocks[0]?.marketo_form_facility_block_type}
        </FacilityModal>
      )}
    </>
  );
};

export default FacilityAccordion;
